import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Instagram, Youtube, ArrowUpRight, Loader2 } from "lucide-react";
import { PROFILE, LINKS, IMAGES } from "@/content";
import { useLanguage } from "@/lang";
import { SectionHeading } from "./SectionHeading";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LINK_META = {
  instagram: { href: LINKS.instagram, icon: Instagram, testid: "contact-link-instagram" },
  tiktok: { href: LINKS.tiktok, icon: Youtube, testid: "contact-link-tiktok" },
  imdb: { href: LINKS.imdb, icon: ArrowUpRight, testid: "contact-link-imdb" },
  linkedin: { href: LINKS.linkedin, icon: ArrowUpRight, testid: "contact-link-linkedin" },
};

export const Contact = () => {
  const { copy } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(copy.contact.success);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(copy.contact.error);
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border border-white/15 px-4 py-3.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/60 transition-[border-color] duration-300";

  return (
    <section id="kontakt" data-testid="contact-section" className="relative border-t border-white/10 overflow-hidden">
      <div className="absolute inset-0">
        <img src={IMAGES.hero} alt="" className="w-full h-full object-cover grayscale opacity-20" />
        <div className="absolute inset-0 bg-black/70" />
      </div>

      <div className="relative max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-32">
        <SectionHeading {...copy.headings.kontakt} />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5 space-y-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <h3 className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-4">{copy.contact.direct}</h3>
              <a
                data-testid="contact-email-link"
                href={`mailto:${PROFILE.email}`}
                className="font-display text-2xl md:text-3xl tracking-tight italic hover:opacity-70 transition-opacity duration-300 break-all"
              >
                {PROFILE.email}
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.1 }}
            >
              <h3 className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-4">{copy.contact.phoneLabel}</h3>
              <a
                data-testid="contact-phone-link"
                href={`tel:${PROFILE.phone.replace(/\s/g, "")}`}
                className="font-display text-2xl md:text-3xl tracking-tight italic hover:opacity-70 transition-opacity duration-300"
              >
                {PROFILE.phone}
              </a>
              <p className="text-sm text-zinc-400 mt-3 leading-relaxed max-w-sm">
                {copy.contact.availability}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
              <h3 className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-4">{copy.contact.profiles}</h3>
              <div className="flex flex-col gap-3">
                {copy.contact.links.map((l) => {
                  const m = LINK_META[l.key];
                  return (
                    <a
                      key={l.key}
                      data-testid={m.testid}
                      href={m.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center justify-between border-b border-white/10 pb-3 text-sm text-zinc-300 hover:text-white transition-colors duration-300"
                    >
                      <span className="flex items-center gap-3">
                        <m.icon size={15} /> {l.label}
                      </span>
                      <ArrowUpRight size={15} className="text-zinc-600 group-hover:text-white group-hover:translate-x-1 group-hover:-translate-y-1 transition-[color,transform] duration-300" />
                    </a>
                  );
                })}
              </div>
            </motion.div>
          </div>

          <motion.form
            data-testid="contact-form"
            onSubmit={submit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-7 bg-black/50 backdrop-blur-xl border border-white/10 p-8 md:p-10 space-y-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <input
                data-testid="contact-form-name"
                required
                value={form.name}
                onChange={update("name")}
                placeholder={copy.contact.form.name}
                className={inputCls}
              />
              <input
                data-testid="contact-form-email"
                required
                type="email"
                value={form.email}
                onChange={update("email")}
                placeholder={copy.contact.form.email}
                className={inputCls}
              />
            </div>
            <input
              data-testid="contact-form-subject"
              value={form.subject}
              onChange={update("subject")}
              placeholder={copy.contact.form.subject}
              className={inputCls}
            />
            <textarea
              data-testid="contact-form-message"
              required
              rows={6}
              value={form.message}
              onChange={update("message")}
              placeholder={copy.contact.form.message}
              className={`${inputCls} resize-none`}
            />
            <button
              data-testid="contact-form-submit"
              type="submit"
              disabled={sending}
              className="group w-full md:w-auto px-10 py-4 bg-white text-black text-xs uppercase tracking-[0.25em] hover:bg-zinc-300 transition-[background-color] duration-300 disabled:opacity-50 flex items-center justify-center gap-3"
            >
              {sending ? <Loader2 size={16} className="animate-spin" /> : null}
              {copy.contact.form.submit}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
